import React, { useState, useRef } from 'react';
import { Row, Col } from 'antd';
import { Checkbox } from 'components/Form/Input';
import { Button, InputRules, TextField, TextFieldPassword, Typography } from 'ui-kit';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
import { getFieldError } from 'components/NewProductStepper/utils';

import ReCAPTCHA from 'react-google-recaptcha';
import { RECAPTCHA_SITE_KEY } from 'consts';

import './SignUpPage.scss';

export interface InitialValues {
  firstName: string;
  email: string;
  password: string;
  phone: string;
  properties: number;
  marketingCheckbox: boolean;
}

interface Props {
  initialValues?: InitialValues;
  onSubmit: (values: InitialValues) => Promise<void>;
}

export default function SignUpForm({ onSubmit }: Props) {
  const { control, handleSubmit, formState } = useFormContext();
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);

  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleLocalSubmit = async (validFormData: any) => {
    try {
      const token = await recaptchaRef.current?.executeAsync();

      if (!token) {
        alert('CAPTCHA failed to generate.');
        return;
      }

      const finalPayload = {
        ...validFormData,
        captchaToken: token,
      };

      await onSubmit(finalPayload);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Submission error:', error);
    } finally {
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(handleLocalSubmit)}>
      <Row gutter={[0, 16]}>
        <Col xs={24}>
          <Controller
            control={control}
            name="firstName"
            render={({ field: { ref, ...rest }, fieldState }) => (
              <TextField
                label="Name"
                fullWidth
                inputRef={ref}
                {...getFieldError(fieldState)}
                {...rest}
              />
            )}
          />
        </Col>
        <Col xs={24}>
          <Controller
            control={control}
            name="email"
            render={({ field: { ref, ...rest }, fieldState }) => (
              <TextField
                label="Email"
                fullWidth
                inputRef={ref}
                {...getFieldError(fieldState)}
                {...rest}
              />
            )}
          />
        </Col>
        <Col xs={24}>
          <Controller
            control={control}
            name="phone"
            render={({ field: { ref, ...rest }, fieldState }) => (
              <TextField
                label="Phone"
                fullWidth
                inputRef={ref}
                {...getFieldError(fieldState)}
                {...rest}
              />
            )}
          />
        </Col>
        <Col xs={24}>
          <Controller
            control={control}
            name="properties"
            render={({ field: { ref, ...rest }, fieldState }) => (
              <TextField
                label="Number of properties managed"
                fullWidth
                type="number"
                inputRef={ref}
                {...getFieldError(fieldState)}
                {...rest}
              />
            )}
          />
        </Col>
        <Col xs={24}>
          <Controller
            control={control}
            name="password"
            render={({ field: { ref, value, onBlur, onChange, ...rest }, fieldState }) => {
              const errors = ((fieldState.error ?? []) as Array<FieldError>).map(
                ({ message }) => message,
              );

              function isValid(key) {
                if (!errors.length) {
                  return Boolean(value);
                }

                return !errors.includes(key);
              }

              return (
                <>
                  <TextFieldPassword
                    label="Password"
                    fullWidth
                    inputRef={ref}
                    value={value}
                    onChange={onChange}
                    onFocus={() => setIsPasswordFocused(true)}
                    onBlur={() => {
                      // Add a small 150ms delay before hiding the rules
                      setTimeout(() => {
                        setIsPasswordFocused(false);
                      }, 150);
                      onBlur();
                    }}
                    {...rest}
                  />
                  <InputRules
                    label="Password must contain:"
                    show={isPasswordFocused}
                    rules={[
                      { message: 'At least 8 characters', isValid: isValid('min-length') },
                      { message: 'One uppercase letter', isValid: isValid('uppercase') },
                      { message: 'One number', isValid: isValid('one-digit') },
                      {
                        message: 'One special character',
                        isValid: isValid('special-character'),
                      },
                    ]}
                  />
                </>
              );
            }}
          />
        </Col>
        <div className="SignupPage-agreement-modal__content">
          <Controller
            name="marketingCheckbox"
            control={control}
            render={({ field }) => (
              <Checkbox
                {...field}
                className="SignupPage-CustomGreyCheckbox"
                checked={field.value}
                onChange={() => {
                  field.onChange(!field.value);
                }}
              />
            )}
          />
          <Typography variant="caption">
            I agree to receive marketing text messages from The Host Co.
          </Typography>
        </div>
        <Col xs={24}>
          <Controller
            control={control}
            name="captchaToken"
            rules={{ required: 'Please complete the CAPTCHA to continue.' }}
            render={() => (
              <div>
                <ReCAPTCHA ref={recaptchaRef} size="invisible" sitekey={RECAPTCHA_SITE_KEY} />
              </div>
            )}
          />
        </Col>
        <Col xs={24}>
          <Button
            variant="contained"
            fullWidth
            disabled={!formState.isValid}
            loading={formState.isSubmitting}
            type="submit"
          >
            Create My Account
          </Button>
        </Col>
      </Row>
    </form>
  );
}
